<template>
    <div v-if="productData" class="page page-product-detail">
        <LayoutProductDetail
            :product-data="productData"
            :product-tab-links="productTabLinks"
            :breadcrumbs="breadcrumbs"
            :reviews="reviews"
            :product-title-suffix="productTitleSuffix"
            :feedback-user="feedbackUser"
            :addon-sale-bundles="addonSaleBundles"
            :discussions-count="discussionsCount"
        />

        <ElementsAskYourPharmacist v-if="productData.showCounsellingBanner" class="mb-4" />
        <ChatBot v-if="chatBotShow" />
        <ElementsCounsellingUpsertModal v-if="productData.showCounsellingBanner" />

        <ModalWatchdogProductModal v-if="watchdogEnabled && productData.status !== 1" :product="productData" />
    </div>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue'
import { onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { MetaObject } from '@nuxt/schema'
import { useNuxtApp } from '#app'
import { safeNavigateTo, transformFullProductToGtmProduct, useRoute } from '#imports'
import GtmPageData from '~/models/DataLayer/GtmPageData'
import DataLayer from '~/extensions/DataLayer'
import FullProduct from '~/models/Product/FullProduct'
import BreadcrumbItem from '~/models/BreadcrumbItem'
import GtmEcommerceEvent from '~/models/DataLayer/GtmEcommerceEvent'
import { GtmEcommerceEventTypeEnum } from '~/models/DataLayer/GtmEcommerceEventTypeEnum'
import AddonSaleBundle from '~/models/Product/AddonSale/AddonSaleBundle'
import ChatBot from '~/components/elements/ChatBot.vue'
import { AddonSaleBundleStrategy } from '~/models/Product/AddonSaleBundleStrategy'
import { useAbTestStore } from '~/stores/abTest'
import PilulkaHeadInfo from '~/models/Head/PilulkaHeadInfo'
import { usePerformanceStore } from '~/stores/performace'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import { useBasketStore } from '~/stores/basket'
import useIsPartOfJsonLd from '~/composables/useIsPartOfJsonLd'
import { useAuthStore } from '~/stores/auth'

const route = useRoute()
const performanceStore = usePerformanceStore()
const { data } = await useAsyncData('count', async () => {
    const route = useRoute()
    const config = useRuntimeConfig()
    const params = route.matched[0].props.default as { [name: string]: string }
    const nuxtApp = useNuxtApp()
    const basketStore = useBasketStore()

    const id = parseInt(params.id)
    const feedbackHash = (route.query.h || null) as string | null

    const productDetailPromise = nuxtApp.$repository.getProductDetailDataAccess(id, basketStore.uid ?? '')
    const reviewPromise = config.public.reviewClubEnabled === true ? nuxtApp.$repository.getProductReviewlist(id) : Promise.resolve([])

    const addonSaleBundlesPromise = nuxtApp.$repository.getAddonSaleBundles(id, AddonSaleBundleStrategy.Nbo)

    const start = performance.now()
    const [productDetailData, reviewResponse, addonSaleBundlesResponse] = await Promise.all([
        productDetailPromise,
        reviewPromise,
        addonSaleBundlesPromise,
    ])
    performanceStore.add('api', performance.now() - start)

    if (feedbackHash) {
        try {
            const startFeedback = performance.now()
            feedbackUser.value = nuxtApp.$repository.getReviewUserByFeedbackHash({
                feedbackHash,
            })
            performanceStore.add('api', performance.now() - startFeedback)
        } catch {}
    }

    return {
        productDetailData,
        reviewResponse,
        addonSaleBundlesResponse,
    }
})

if (!data.value) {
    throw createError({ statusCode: 404, statusMessage: 'Product not found' })
}

const productDetailData = data.value.productDetailData
const reviewResponse = data.value.reviewResponse
const addonSaleBundlesResponse = data.value.addonSaleBundlesResponse

const reviews = ref(reviewResponse)
if (!productDetailData.product) {
    throw createError({ statusCode: 404, statusMessage: 'Product not found' })
}

const productData: Ref<FullProduct> = ref(productDetailData.product)
const productTabLinks = ref(productDetailData.productTabLinks)
const breadcrumbs: Ref<BreadcrumbItem[]> = ref(productData.value.breadcrumbs || [])

const discussionsCount = ref(0)
try {
    const authStore = useAuthStore()
    const accessToken: string | null = authStore.accessToken

    const discussions = await useNuxtApp().$repository.getSelectedDiscussionsList(productData.value.id, null, { accessToken })
    discussionsCount.value = discussions.length
} catch {}

if (
    (productTabLinks.value['tab-reviews'] === route.fullPath && productDetailData.product.comments.total === 0) ||
    (productTabLinks.value['tab-leaflet'] === route.fullPath && !productDetailData.product.showLeaflet) ||
    (productTabLinks.value['tab-discussion'] === route.fullPath && discussionsCount.value === 0)
) {
    await safeNavigateTo(productData.value.url, { redirectCode: 301 })
}

if (productData.value.redirectTo !== null && productData.value.redirectTo !== '') {
    await safeNavigateTo(productData.value.redirectTo, {
        redirectCode: 301,
    })
}

if (productData.value.status === 4) {
    if (productData.value?.productVariants?.recommendedVariantUrl) {
        await safeNavigateTo(productData.value.productVariants.recommendedVariantUrl, { replace: true })
    } else {
        throw createError({ statusCode: 404, statusMessage: 'Product not found' })
    }
}

const addonSaleBundles: Ref<AddonSaleBundle[]> = ref(addonSaleBundlesResponse.bundles)
const feedbackUser = ref({})

const nuxtApp = useNuxtApp()

const productTitleSuffix = computed((): string => {
    const route = useRoute()

    const tabNamesByLinks = Object.fromEntries(Object.entries(productTabLinks.value || {}).map((tab) => tab.reverse()))
    const activeTab = tabNamesByLinks[route.fullPath] || 'tab-all'

    if (activeTab === 'tab-all') {
        return ''
    }

    const camelCasedActiveTab = activeTab.toLowerCase().replace(/([-_][a-z])/g, (group: string) => group.toUpperCase().replace(/[-_]/g, ''))
    return 'productDetail.tabs.productTitleSuffixes.' + camelCasedActiveTab
})

const i18n = useI18n()

const chatBotShow = computed(() => {
    if (config.public.isChatBotEnabled && (i18n.tm('chatBot.product') as number[]).includes(productData.value.id) && metaObject.script) {
        metaObject.script.push({
            type: 'text/javascript',
            src: 'https://static.bots.sefbot.cz/website/js/widget2.db8fbf7f.min.js',
            // @ts-ignore
            crossorigin: 'crossorigin',
            async: true,
            defer: true,
            'data-no-minify': '1',
        })

        return true
    }

    return false
})

const provideMetaObject = (): MetaObject => {
    try {
        const metaObject = nuxtApp.$constructHead({
            headInfo: route.meta.headInfo as PilulkaHeadInfo,
            customTitle: productData.value.name + (productTitleSuffix.value === '' ? '' : i18n.t(productTitleSuffix.value)),
            customMetaDescription: productData.value.metaDescription,
            preloadFiles: productData.value.imagesOverviews.slice(0, 4),
            image: productData.value.images[0] || null,
            ogType: '',
        })
        metaObject.link?.push({
            rel: process.server ? 'preload' : 'stylesheet',
            href: '/dist/rich-content-css.css?v=1',
            as: 'style',
            onload: "this.onload=null;this.rel='stylesheet'",
        })
        metaObject.script?.push({
            src: '/rich-content/js/app.js',
            // @ts-ignore
            body: true,
            async: true,
        })

        return metaObject
    } catch (e) {
        return {}
    }
}

const shouldNginxCacheBeDisabled = () => {
    const route = useRoute()
    const path = route.fullPath.split('?')[0]
    const tabNamesByLinks = Object.fromEntries(Object.entries(productTabLinks.value).map((tab) => tab.reverse()))

    return tabNamesByLinks[path] === 'tab-discussion'
}

const metaObject = provideMetaObject()
if (shouldNginxCacheBeDisabled()) {
    metaObject.bodyAttrs = {
        'data-no-cache': true,
    }
}

const config = useRuntimeConfig()

const watchdogEnabled = config.public.watchdogEnabled === true

onMounted(async () => {
    refreshFullProductFrequentlyActualizedData([productData.value])

    const basketStore = useBasketStore()

    if (window) {
        const nuxtApp = useNuxtApp()
        const lazyload = nuxtApp.$lazyload
        const media = document.querySelectorAll('.lazyload')
        media.forEach((m) => lazyload(m as HTMLElement))

        // do not hide modal when there should be rating shown
        if (!useRoute()?.query['rate-bought']) {
            nuxtApp.$bus.$emit('hideModal', 'modal-prebasket')
        }
    }

    const { categoryNames, breadcrumbs } = productData.value

    const page = {
        type: 'product',
        title: (metaObject.title as string).trim(),
        categories: [...categoryNames],
        breadcrumbs,
    } as GtmPageData

    const ecommerceEvent = {
        type: GtmEcommerceEventTypeEnum.ViewProductDetail,
        products: [transformFullProductToGtmProduct(productData.value)],
    } as GtmEcommerceEvent

    setTimeout(() => {
        DataLayer.pushPageViewToDataLayer(page)
    }, 0)
    setTimeout(() => {
        DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)
    }, 250)

    const abTestStore = useAbTestStore()
    const isBundleStrategyBundle =
        abTestStore.getVariant('DynamicBundleAbTestV2') === 3 || abTestStore.getVariant('DynamicBundleAbTestV2') === 2
    if (isBundleStrategyBundle) {
        const bundleResponse = await nuxtApp.$repository.getAddonSaleBundles(
            productData.value.id,
            AddonSaleBundleStrategy.Bundle,
            basketStore.getProductIds()
        )
        addonSaleBundles.value = bundleResponse.bundles
    } else {
        const bundleResponse = await nuxtApp.$repository.getAddonSaleBundles(
            productData.value.id,
            AddonSaleBundleStrategy.Nbo,
            basketStore.getProductIds()
        )
        addonSaleBundles.value = bundleResponse.bundles
    }

    LuigisBoxEventApi.sendPageView()

    addClosePageRegistrationEvent()
})

useHead(metaObject)

const breadcrumbsWithProduct = [...breadcrumbs.value]
breadcrumbsWithProduct.push({
    url: productData.value.url,
    title: productData.value.name,
})

const priceForJsonLdSchema = () => {
    if (productData.value.mobileAppPrice && productData.value.productCodeSale && productData.value.productCodeSale.hasCodeSale) {
        return productData.value.mobileAppPrice <= productData.value.productCodeSale.salePrice
            ? productData.value.mobileAppPrice
            : productData.value.productCodeSale.salePrice
    }

    return (
        productData.value.mobileAppPrice ??
        (productData.value.productCodeSale && productData.value.productCodeSale.hasCodeSale
            ? productData.value.productCodeSale.salePrice
            : productData.value.price)
    )
}

const jsonldFunction = () => {
    if (!productData.value.name || productData.value.status !== 1) {
        return null
    }

    const productUrl = `${config.public.apiUrl}${productData.value.url}`

    const commentsStructuredData = productData.value.comments.comments
        .filter(function (item) {
            return item.name.length > 2 && item.text.length && item.rating !== 0 && item.rating <= 5
        })
        .map((item) => ({
            '@type': 'Review',
            datePublished: item.updatedAt,
            reviewRating: {
                '@type': 'Rating',
                bestRating: 5,
                ratingValue: item.rating,
                worstRating: 1,
            },
            name: productData.value.name,
            author: {
                '@type': 'Person',
                name: item.name || 'Anonym',
            },
            reviewBody: item.text,
        }))

    const productSchemaData: any = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: productData.value.name,
        '@id': `${productUrl}#product`,
        image: productData.value.images
            .map((val) => {
                return val
            })
            .filter((image) => image.length > 1),
        description: productData.value.content.shortNoFullHtml,
        gtin: productData.value.attributes.ean,
        category: breadcrumbs.value.map((item) => item.title).join(' / '),
        review: commentsStructuredData,
        offers: {
            '@type': 'Offer',
            url: productUrl,
            priceCurrency: config.public.currency,
            price: priceForJsonLdSchema(),
            itemCondition: productData.value.isSecondQuality ? 'https://schema.org/UsedCondition' : 'https://schema.org/NewCondition',
            availability: productData.value.amountInStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
            shippingDetails: productData.value.shippings.map((shipping) => {
                return {
                    '@type': 'OfferShippingDetails',
                    shippingLabel: shipping.title,
                    shippingRate: {
                        '@type': 'MonetaryAmount',
                        value: shipping.price,
                        currency: config.public.currency,
                    },
                    shippingDestination: {
                        '@type': 'DefinedRegion',
                        addressCountry: i18n.t('structuredData.shippingCountry'),
                    },
                }
            }),
        },
        mainEntityOfPage: {
            '@type': 'ItemPage',
            headline: productData.value.name,
            url: productUrl,
            '@id': `${productUrl}#itempage`,
            about: { '@id': `${productUrl}#product` },
            mainEntity: { '@id': `${productUrl}#product` },
            inLanguage: i18n.t('locale'),
            potentialAction: {
                '@type': 'SellAction',
                object: {
                    '@type': 'Product',
                    name: productData.value.name,
                    '@id': `${productUrl}#product`,
                },
            },
            keywords: productData.value.name,
            breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbsWithProduct
                    .map((item, index) => ({
                        '@type': 'ListItem',
                        position: index + 1,
                        item: {
                            '@id': `${config.public.apiUrl}${item.url}`,
                            name: item.title,
                        },
                    }))
                    .filter((breadcrumb) => breadcrumb.item.name.length > 1),
            },
            isPartOf: useIsPartOfJsonLd(),
        },
    }

    if (productData.value.productRating && productData.value.productRating.totalReviews > 0) {
        productSchemaData.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: productData.value.productRating.averageRating,
            reviewCount: productData.value.productRating.totalReviews,
        }
    }

    if (productData.value.brandName !== null) {
        productSchemaData.brand = {
            '@type': 'Brand',
            name: productData.value.brandName,
        }
    }

    if (productData.value.manufacturerName !== null) {
        productSchemaData.manufacturer = {
            '@type': 'Organization',
            name: productData.value.manufacturerName,
        }
    }

    return productSchemaData
}

useJsonld(jsonldFunction())

const isUserAuthenticated = () => {
    return useBasketStore().authUser !== null
}

const registrationEventCallback = (event: MouseEvent) => {
    const from = event.relatedTarget
    const registrationModalCookie = useRegistrationModalCookie()

    if (!from && event.clientY <= 100 && !registrationModalCookie.value.modalShown) {
        registrationModalCookie.value.modalShown = true
        useNuxtApp().$bus.$emit('showRegistrationModal', {
            eventOpenType: 'onMouse',
        })
    }
}
const addClosePageRegistrationEvent = () => {
    const registrationModalCookie = useRegistrationModalCookie()
    if (isUserAuthenticated() || registrationModalCookie.value.modalShown) {
        //  && window.referrer !== productData.value.url
        return
    }

    document.addEventListener('mouseout', registrationEventCallback)
}
onUnmounted(() => {
    document.removeEventListener('mouseout', registrationEventCallback)
})
</script>
